/**
 * Merchant User Token Manager
 * 管理按 merchantId 儲存的 merchant_user_token
 * 支援多 tab 多 merchant 共存
 */

// Token key 前綴
const TOKEN_KEY_PREFIX = 'merchantUserToken';

// 安全地存取 localStorage
const safeGetStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (err) {
    console.warn('localStorage not available', err);
    return null;
  }
};

// 建立 token key
const buildTokenKey = (userId, merchantId) => {
  if (!userId || !merchantId) return null;
  return `${TOKEN_KEY_PREFIX}:${userId}:${merchantId}`;
};

/**
 * 解析 JWT token（不驗證簽名，僅解析）
 * @param {string} token - JWT token
 * @returns {object|null} - Decoded payload
 */
const decodeToken = (token) => {
  if (!token || typeof token !== 'string') return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (err) {
    console.warn('Failed to decode token', err);
    return null;
  }
};

/**
 * 檢查 token 是否過期
 * @param {object} payload - Decoded token payload
 * @returns {boolean} - true if expired
 */
const isTokenExpired = (payload) => {
  if (!payload || !payload.exp) return true;

  const now = Math.floor(Date.now() / 1000);
  // 提前 5 分鐘視為過期，避免邊界問題
  return payload.exp - now < 300;
};

/**
 * 儲存 merchant_user_token
 * @param {string} userId - User ID
 * @param {string} merchantId - Merchant ID
 * @param {string} token - JWT token
 * @returns {boolean} - Success
 */
export const setMerchantUserToken = (userId, merchantId, token) => {
  const key = buildTokenKey(userId, merchantId);
  if (!key || !token) return false;

  const storage = safeGetStorage();
  if (!storage) return false;

  try {
    storage.setItem(key, token);
    return true;
  } catch (err) {
    console.warn('Failed to store merchant user token', err);
    return false;
  }
};

/**
 * 獲取 merchant_user_token（自動檢查過期）
 * @param {string} userId - User ID
 * @param {string} merchantId - Merchant ID
 * @returns {string|null} - Token or null if not found/expired
 */
export const getMerchantUserToken = (userId, merchantId) => {
  const key = buildTokenKey(userId, merchantId);
  if (!key) return null;

  const storage = safeGetStorage();
  if (!storage) return null;

  try {
    const token = storage.getItem(key);
    if (!token) return null;

    // 檢查是否過期
    const payload = decodeToken(token);
    if (!payload || isTokenExpired(payload)) {
      // 過期，自動清除
      storage.removeItem(key);
      return null;
    }

    return token;
  } catch (err) {
    console.warn('Failed to get merchant user token', err);
    return null;
  }
};

/**
 * 清除指定 merchant 的 token
 * @param {string} userId - User ID
 * @param {string} merchantId - Merchant ID
 */
export const clearMerchantUserToken = (userId, merchantId) => {
  const key = buildTokenKey(userId, merchantId);
  if (!key) return;

  const storage = safeGetStorage();
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (err) {
    console.warn('Failed to clear merchant user token', err);
  }
};

/**
 * 清除用戶的所有 merchant tokens
 * @param {string} userId - User ID
 */
export const clearAllMerchantUserTokens = (userId) => {
  if (!userId) return;

  const storage = safeGetStorage();
  if (!storage) return;

  try {
    const prefix = `${TOKEN_KEY_PREFIX}:${userId}:`;
    const keys = Object.keys(storage);

    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        storage.removeItem(key);
      }
    });
  } catch (err) {
    console.warn('Failed to clear all merchant user tokens', err);
  }
};

/**
 * 清除所有 merchant tokens（不需要 userId）
 * 用於 logout 時清除所有 token
 */
export const clearAllTokens = () => {
  const storage = safeGetStorage();
  if (!storage) return;

  try {
    const keys = Object.keys(storage);

    keys.forEach(key => {
      if (key.startsWith(TOKEN_KEY_PREFIX)) {
        storage.removeItem(key);
      }
    });
  } catch (err) {
    console.warn('Failed to clear all tokens', err);
  }
};

/**
 * 解析 token 並返回 payload
 * @param {string} token - JWT token
 * @returns {object|null} - Decoded payload
 */
export const parseMerchantUserToken = (token) => {
  return decodeToken(token);
};

/**
 * 獲取 token 中的 permissions
 * @param {string} token - JWT token
 * @returns {array} - Permissions array
 */
export const getPermissionsFromToken = (token) => {
  const payload = decodeToken(token);
  return payload?.permissions || [];
};

export default {
  setMerchantUserToken,
  getMerchantUserToken,
  clearMerchantUserToken,
  clearAllMerchantUserTokens,
  clearAllTokens,
  parseMerchantUserToken,
  getPermissionsFromToken,
};
